import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import './OpTextBox.css';

/**
 * OpTextBox - a native input element.
 *
 * Provides a standardized input field with support for:
 * - Label rendering (top or inside positioning)
 * - Required indicator (asterisk)
 * - Error message display
 * - Disabled state styling
 * - Optional numeric-only input enforcement
 *
 * Features:
 * - Manages its own internal state, meaning the parent does not need to feed the value back
 * - `value` acts as an initial preload
 * - `onChange` notifies the parent of changes
 *
 * Props:
 *
 * @param {string} className
 * A className prop for the input element.
 * @param {boolean} [enabled=true]
 * Controls whether the input is enabled or disabled.
 * @param {boolean} [enableNumbersOnly=false]
 * Restricts input to numeric characters only (0-9).
 * @param {string} [errorMessage]
 * Displays an error message below the input and applies error styling.
 * @param {string} label
 * Text label displayed above or inside the input depending on `labelPosition`.
 * @param {'Top'|'Inside'} [labelPosition='Top']
 * Determines label placement relative to the input field.
 * @param {function} [onChange]
 * Callback fired when the value changes. Receives the updated string value.
 * @param {string} placeholder
 * The display text when there is no value for the input field.
 * @param {boolean} [showAsterisk]
 * If true, displays a required field asterisk (*) next to the label.
 * @param {string} [value] Initial preload value of the input field.
 */
export default function OpTextBox({
  className,
  enabled = true,
  enableNumbersOnly = false,
  errorMessage,
  label,
  labelPosition = 'Top',
  onChange,
  placeholder,
  showAsterisk = false,
  value,
}) {
  const [internalValue, setInternalValue] = useState(value ?? '');

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const isDisabled = !enabled;
  const isLabelInside = labelPosition === 'Inside';

  const handleChange = (e) => {
    let currentValue = e.target.value;

    if (enableNumbersOnly) {
      currentValue = currentValue.replace(/\D/g, '');
      e.target.value = currentValue;
    }

    setInternalValue(currentValue);

    onChange?.(currentValue, e);
  };

  return (
    <div
      className={clsx(
        'op-textbox-wrapper',
        isLabelInside && 'op-textbox-wrapper-label-inside',
        isDisabled && 'op-textbox-wrapper-disabled',
        isDisabled
          && isLabelInside
          && 'op-textbox-wrapper-disabled-label-inside',
      )}
    >
      {label && (
        <label
          className={clsx(
            'op-textbox-label',
            isLabelInside && 'op-textbox-label-inside',
          )}
        >
          {label}
          {showAsterisk && <span className="op-textbox-asterisk">*</span>}
        </label>
      )}

      <input
        className={clsx(
          'op-textbox',
          className,
          errorMessage && 'op-textbox-error-input',
          isLabelInside && 'op-textbox-label-inside-input',
        )}
        disabled={isDisabled}
        onChange={handleChange}
        placeholder={placeholder}
        value={internalValue}
      />

      {errorMessage && (
        <span className="op-textbox-error-message">{errorMessage}</span>
      )}
    </div>
  );
}

OpTextBox.propTypes = {
  className: PropTypes.string,
  enabled: PropTypes.bool,
  enableNumbersOnly: PropTypes.bool,
  errorMessage: PropTypes.string,
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(['Top', 'Inside']),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  showAsterisk: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

OpTextBox.defaultProps = {
  className: undefined,
  enabled: true,
  enableNumbersOnly: false,
  errorMessage: undefined,
  label: undefined,
  labelPosition: 'Top',
  onChange: undefined,
  placeholder: undefined,
  showAsterisk: false,
  value: undefined,
};
